import dayjs from 'dayjs';
import { StorageContainerLabelData } from '../types/storageLabel';

/**
 * 仓储标签工具函数
 * 提供标签处理相关的通用功能
 */

/**
 * 下载Blob文件
 */
export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * 生成标签文件名
 */
export const generateLabelFilename = (
  labelData: StorageContainerLabelData,
  suffix: string = '',
  extension: string = 'pdf'
): string => {
  const timestamp = dayjs().format('YYYYMMDDHHmmss');
  const containerCode = labelData.containerInfo.systemCode;
  const baseFilename = `仓储容器标签_${containerCode}_${timestamp}`;
  
  return suffix 
    ? `${baseFilename}_${suffix}.${extension}`
    : `${baseFilename}.${extension}`;
};

/**
 * 验证标签数据的完整性
 */
export const validateLabelData = (data: StorageContainerLabelData): {
  isValid: boolean;
  errors: string[];
} => {
  const errors: string[] = [];

  // 检查基本字段
  if (!data.id) errors.push('缺少标签ID');
  if (!data.templateId) errors.push('缺少模板ID');
  
  // 检查容器信息
  if (!data.containerInfo) {
    errors.push('缺少容器信息');
  } else {
    if (!data.containerInfo.id) errors.push('缺少容器ID');
    if (!data.containerInfo.systemCode) errors.push('缺少容器系统编码');
    if (!data.containerInfo.name) errors.push('缺少容器名称');
    if (!data.containerInfo.warehouse) errors.push('缺少仓库信息');
    if (!data.containerInfo.project) errors.push('缺少项目信息');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * 格式化打印选项
 */
export const formatPrintOptions = (options?: {
  copies?: number;
  directPrint?: boolean;
}): { copies: number; directPrint: boolean } => {
  return {
    copies: options?.copies || 1,
    directPrint: options?.directPrint !== false // 默认为true
  };
};

/**
 * 创建标签预览URL的清理函数
 */
export const createPreviewUrlCleaner = () => {
  const urls: string[] = [];
  
  return {
    add: (url: string) => {
      urls.push(url);
    },
    cleanup: () => {
      urls.forEach(url => {
        try {
          URL.revokeObjectURL(url);
        } catch (error) {
          console.warn('清理预览URL失败:', url, error);
        }
      });
      urls.length = 0;
    }
  };
};

/**
 * 格式化内容摘要为显示文本
 */
export const formatContentSummary = (summary?: {
  totalItems: number;
  itemTypes: string[];
  totalQuantity: number;
}): string => {
  if (!summary) return '无内容信息';
  
  const { totalItems, itemTypes, totalQuantity } = summary;
  
  if (totalItems === 0) return '空容器';
  
  const itemTypesText = itemTypes.length > 0 
    ? itemTypes.slice(0, 2).join(', ') + (itemTypes.length > 2 ? '等' : '')
    : '未知类型';
    
  return `${totalItems}项/${totalQuantity}个 (${itemTypesText})`;
};

/**
 * 检查是否支持打印功能
 */
export const checkPrintSupport = (): {
  supported: boolean;
  message: string;
} => {
  // 检查是否在支持打印的环境中
  if (typeof window === 'undefined') {
    return {
      supported: false,
      message: '服务端环境不支持打印'
    };
  }

  if (!window.print) {
    return {
      supported: false,
      message: '浏览器不支持打印功能'
    };
  }

  // 可以添加更多的打印支持检查
  return {
    supported: true,
    message: '支持打印功能'
  };
};

/**
 * 计算标签尺寸（像素）
 */
export const calculateLabelPixelSize = (
  widthMM: number,
  heightMM: number,
  dpi: number = 300
): { width: number; height: number } => {
  const mmToInch = 1 / 25.4;
  const width = Math.round(widthMM * mmToInch * dpi);
  const height = Math.round(heightMM * mmToInch * dpi);
  
  return { width, height };
};

/**
 * 生成标签批次ID
 */
export const generateBatchId = (): string => {
  return `batch_${dayjs().format('YYYYMMDDHHmmss')}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * 延迟执行函数（用于防抖）
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

/**
 * 错误处理工具
 */
export const handleLabelError = (error: any, operation: string): {
  message: string;
  details: string;
} => {
  console.error(`[标签${operation}失败]:`, error);
  
  let message = `标签${operation}失败`;
  let details = '未知错误';
  
  if (error?.response?.data?.message) {
    details = error.response.data.message;
  } else if (error?.message) {
    details = error.message;
  } else if (typeof error === 'string') {
    details = error;
  }
  
  return { message, details };
};
